// Image preparation for direct-to-GCS uploads.
//
// Image bytes are PUT straight to the bucket via signed URLs, bypassing the API
// gateway's request-size limit — so there is no tiny budget to compress down to.
// We compress only gently: pass already-reasonable web images through untouched
// (best quality, no re-encode), and otherwise downscale once to a generous max
// dimension. A thrown "UNSUPPORTED_IMAGE" means the browser couldn't decode the
// file (e.g. HEIC without support); callers surface a friendly message.

const MB = 1024 * 1024;

const DIRECT_UPLOAD_MAX_DIMENSION = 2048;
const DIRECT_UPLOAD_QUALITY = 0.85;
const DIRECT_UPLOAD_PASSTHROUGH_MAX_BYTES = 8 * MB;
const PASSTHROUGH_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const getImageDimensions = async (
  file: File
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error(`Could not load image ${file.name}`));
    };

    image.src = objectUrl;
  });

const getTargetSize = (
  width: number,
  height: number,
  maxDimension: number
): { width: number; height: number } => {
  const largest = Math.max(width, height);

  if (largest <= maxDimension) {
    return { width, height };
  }

  const ratio = maxDimension / largest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
};

const canvasToJpegBlob = (
  canvas: HTMLCanvasElement,
  quality: number
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("Could not compress image"));
      },
      "image/jpeg",
      quality
    );
  });

const getUploadFileName = (file: File, index: number): string => {
  const fallbackBase = `image${index}`;
  const baseName = file.name.replace(/\.[^/.]+$/, "") || fallbackBase;
  return `${baseName}.jpg`;
};

const encodeToJpeg = async (
  file: File,
  index: number,
  maxDimension: number,
  quality: number
): Promise<File> => {
  let dimensions: { width: number; height: number };
  try {
    dimensions = await getImageDimensions(file);
  } catch {
    throw new Error("UNSUPPORTED_IMAGE");
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return file;
  }

  const target = getTargetSize(dimensions.width, dimensions.height, maxDimension);
  canvas.width = target.width;
  canvas.height = target.height;

  const image = new Image();
  const objectUrl = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error("UNSUPPORTED_IMAGE"));
      image.src = objectUrl;
    });

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, target.width, target.height);
    context.drawImage(image, 0, 0, target.width, target.height);

    const blob = await canvasToJpegBlob(canvas, quality);
    return new File([blob], getUploadFileName(file, index), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Gently prepare an image for direct upload: pass reasonable JPEG/PNG/WebP
 * through untouched, otherwise downscale once to a generous max + encode JPEG.
 */
export const prepareImageForDirectUpload = async (
  file: File,
  index = 0
): Promise<File> => {
  if (
    PASSTHROUGH_TYPES.has(file.type.toLowerCase()) &&
    file.size > 0 &&
    file.size <= DIRECT_UPLOAD_PASSTHROUGH_MAX_BYTES
  ) {
    try {
      const { width, height } = await getImageDimensions(file);
      if (Math.max(width, height) <= DIRECT_UPLOAD_MAX_DIMENSION) {
        return file;
      }
    } catch {
      // Couldn't measure — fall through to a single re-encode pass.
    }
  }

  return encodeToJpeg(
    file,
    index,
    DIRECT_UPLOAD_MAX_DIMENSION,
    DIRECT_UPLOAD_QUALITY
  );
};
